/**
 * Dynamics, words and the lines: the marks that hang off notes rather than being part of
 * them.
 *
 * Two kinds live here and they are placed differently. A **direction** -- a dynamic or a
 * word -- happens at a point, so a passage selected across four parts takes one mark per
 * part, on the first note of the passage in each. A **spanner** -- a slur, a hairpin, a
 * glissando, a pedal line or an octave sign -- runs between two ends, so the same passage
 * takes one per part, from its first note to its last. Neither is drawn across parts,
 * because neither means anything across parts.
 *
 * Every function answers with a command or with a refusal in words, as the rest of the
 * editing layer does, and nothing here reaches into the document itself.
 */

import { createId } from "../document/ids";
import type { EventId, MeasureId, PartId } from "../document/ids";
import type {
  Direction,
  DynamicMark,
  OctaveShift,
  Score,
  Spanner,
  SpannerKind,
  WordsStyle,
} from "../document/types";
import type { Command } from "../commands/types";
import { selectedLocations } from "./selection";
import type { Selection } from "./selection";

/** Where a mark is drawn relative to the staff. */
export type Placement = "above" | "below";

/** One part's share of a selection: the bar the passage starts in, and both its ends. */
interface Passage {
  readonly partId: PartId;
  readonly measureId: MeasureId;
  readonly from: EventId;
  readonly to: EventId;
}

/**
 * The selection split by part, in reading order.
 *
 * Each part contributes at most one passage even when the selection covers several of its
 * bars, because that is what a mark applies to: a crescendo over bars 3 to 5 is one
 * hairpin, not three.
 */
function passages(score: Score, selection: Selection): readonly Passage[] {
  const byPart = new Map<PartId, Passage>();
  for (const location of selectedLocations(score, selection)) {
    const held = byPart.get(location.partId);
    if (held === undefined) {
      byPart.set(location.partId, {
        partId: location.partId,
        measureId: location.measureId,
        from: location.event.id,
        to: location.event.id,
      });
      continue;
    }
    byPart.set(location.partId, { ...held, to: location.event.id });
  }
  return [...byPart.values()];
}

/** Put a dynamic on the first selected note of every part the selection touches. */
export function dynamic(
  score: Score,
  selection: Selection,
  mark: DynamicMark,
  placement: Placement = "below",
): Command | string {
  return direction(score, selection, { kind: "dynamic", mark }, placement, `dynamic ${mark}`);
}

/** Where each style of text stands when the caller does not say. */
const WORDS_PLACEMENT: Readonly<Record<WordsStyle, Placement>> = {
  expression: "below",
  technique: "above",
  tempo: "above",
};

/**
 * Write words at the start of the selection, in one of the three styles a score uses.
 *
 * The style is the whole of what is being said: *dolce* is an expression and goes under the
 * staff in italics, `pizz.` is a technique and goes over it upright, **rit.** is about speed
 * and goes over it in bold. A caller that says nothing gets the style's own side of the
 * staff, which is what a player expects to read it at.
 */
export function words(
  score: Score,
  selection: Selection,
  text: string,
  style: WordsStyle = "technique",
  placement: Placement = WORDS_PLACEMENT[style],
): Command | string {
  const trimmed = text.trim();
  if (trimmed === "") return "there are no words to write";
  const content = { kind: "words", text: trimmed, style } as const;
  return direction(score, selection, content, placement, `${style} text`);
}

function direction(
  score: Score,
  selection: Selection,
  content: Direction["content"],
  placement: Placement,
  label: string,
): Command | string {
  const spans = passages(score, selection);
  if (spans.length === 0) return "nothing is selected";
  return {
    kind: "batch",
    label,
    commands: spans.map((span) => ({
      kind: "addDirection" as const,
      partId: span.partId,
      measureId: span.measureId,
      direction: {
        id: createId("direction"),
        anchor: span.from,
        placement,
        content,
      },
    })),
  };
}

/** Slur the selection in each part it covers. */
export function slur(
  score: Score,
  selection: Selection,
  placement: Placement = "above",
): Command | string {
  return spanner(score, selection, "slur", placement, "slur");
}

/** Open a hairpin across the selection in each part it covers. */
export function hairpin(
  score: Score,
  selection: Selection,
  kind: "crescendo" | "diminuendo",
  placement: Placement = "below",
): Command | string {
  return spanner(score, selection, kind, placement, kind);
}

/**
 * Slide from the first selected note to the last, in each part the selection covers.
 *
 * A glissando is drawn between two note heads, so it has no side of the staff to be on; it
 * is stored `above` and every reader draws it the same way.
 */
export function glissando(score: Score, selection: Selection): Command | string {
  return spanner(score, selection, "glissando", "above", "glissando");
}

/** Hold the pedal down across the selection, in each part it covers. */
export function pedal(
  score: Score,
  selection: Selection,
  placement: Placement = "below",
): Command | string {
  return spanner(score, selection, "pedal", placement, "pedal");
}

/**
 * Put an octave sign over the selection.
 *
 * The sign's own name says which side of the staff it goes: `8va` and `15ma` are written
 * above the notes and `8vb` and `15mb` below them, so there is nothing to ask about.
 *
 * It changes the page and not the music. The pitches stay as they are and the sign moves
 * them an octave when they are drawn, which is what makes a high passage readable without
 * ledger lines and is why playback sounds exactly the same after it is added.
 */
export function octave(score: Score, selection: Selection, kind: OctaveShift): Command | string {
  const placement = kind === "8va" || kind === "15ma" ? "above" : "below";
  return spanner(score, selection, kind, placement, kind);
}

function spanner(
  score: Score,
  selection: Selection,
  kind: SpannerKind,
  placement: Placement,
  label: string,
): Command | string {
  const spans = passages(score, selection).filter((span) => span.from !== span.to);
  if (spans.length === 0) return "a line needs two notes to run between";
  return {
    kind: "batch",
    label,
    commands: spans.map((span) => ({
      kind: "addSpanner" as const,
      partId: span.partId,
      spanner: {
        id: createId("spanner"),
        kind,
        from: span.from,
        to: span.to,
        placement,
      } satisfies Spanner,
    })),
  };
}

/**
 * Take away every dynamic, word, slur and hairpin the selection touches.
 *
 * A line counts as touched when either of its ends is selected, which is how a user reads
 * it: clicking the note a slur starts on and pressing delete should take the slur away,
 * not leave it hanging from a note that is no longer under it.
 */
export function clearMarks(score: Score, selection: Selection): Command | string {
  const commands: Command[] = [];
  for (const part of score.parts) {
    for (const spanner_ of part.spanners) {
      if (!selection.events.has(spanner_.from) && !selection.events.has(spanner_.to)) continue;
      commands.push({ kind: "removeSpanner", partId: part.id, spannerId: spanner_.id });
    }
    for (const global of score.measures) {
      const measure = part.measures[global.id];
      if (measure === undefined) continue;
      for (const held of measure.directions) {
        if (!selection.events.has(held.anchor)) continue;
        commands.push({
          kind: "removeDirection",
          partId: part.id,
          measureId: global.id,
          directionId: held.id,
        });
      }
    }
  }
  if (commands.length === 0) return "there is nothing on the selection to take away";
  return { kind: "batch", label: "clear marks", commands };
}
